import type { CitationCard, ConversationMessage, PendingDisclosure, ProviderStatusKind } from './types'
import { DEFAULT_SCOPE, GENERATION_PURPOSE } from './types'

export function safeText(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return value.map((v) => safeText(v)).filter(Boolean).join(' ')
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>
    const picks = [obj.en, obj.ru, obj.es, obj.title, obj.name]
    for (const p of picks) {
      if (typeof p === 'string' && p.trim()) return p
    }
    const firstString = Object.values(obj).find((entry) => typeof entry === 'string' && entry.trim())
    if (typeof firstString === 'string') return firstString
  }
  return ''
}

export function dedupeCitations(list: CitationCard[]): CitationCard[] {
  const seen = new Set<string>()
  const out: CitationCard[] = []
  for (const c of list || []) {
    const label = safeText(c.citationLabel || '')
    const locator = [safeText(c.standardCode), safeText(c.controlCode || c.articleRef || c.clauseRef)]
      .filter(Boolean)
      .join(':')
    const key =
      c.sourceType === 'STANDARD_CORPUS'
        ? (locator || label).toUpperCase()
        : `${label}|${safeText(c.excerpt || '').toLowerCase()}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(c)
  }
  return out
}

export function buildCitationPrimary(c: CitationCard): string {
  const label = safeText(c.citationLabel)
  if (label) return label
  const standardLocator = [safeText(c.standardCode), safeText(c.controlCode || c.articleRef)].filter(Boolean).join(' ')
  if (standardLocator) return standardLocator
  return safeText(c.filename || c.sourceEntityId || c.sourceType || 'Reference')
}

export function buildCitationSecondary(c: CitationCard, primary: string): string {
  if (c.sourceType === 'STANDARD_CORPUS') {
    const excerpt = safeText(c.excerpt || c.section || '')
    if (!excerpt) return ''
    const primaryNorm = primary.toLowerCase().replace(/\s+/g, ' ').trim()
    const excerptNorm = excerpt.toLowerCase().replace(/\s+/g, ' ').trim()
    if (excerptNorm === primaryNorm) return ''
    if (primaryNorm.includes(excerptNorm)) return ''
    const standardOnly = safeText(c.standardCode)
    if (excerptNorm === standardOnly.toLowerCase()) return ''
    return excerpt
  }
  const secondary = safeText(c.excerpt || c.section || c.filename || '')
  if (!secondary) return ''
  const primaryNorm = primary.toLowerCase().replace(/\s+/g, ' ').trim()
  const secondaryNorm = secondary.toLowerCase().replace(/\s+/g, ' ').trim()
  if (secondaryNorm === primaryNorm) return ''
  const standardOnly = safeText(c.standardCode)
  const filenameOnly = safeText(c.filename)
  if (secondaryNorm === standardOnly.toLowerCase() || secondaryNorm === filenameOnly.toLowerCase()) return ''
  if (primaryNorm.includes(secondaryNorm)) return ''
  return secondary
}

export function formatDate(value: string | null | undefined) {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString()
}

export function scopeLabel(scope: string) {
  if (!scope || scope === DEFAULT_SCOPE) return 'All organization data'
  return scope.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export function cleanAssistantContent(content: string): string {
  return content
    .replace(/AI foundation response \(simulated\):/gi, '')
    .replace(/Job:\s*(governance_organization_conversation|document_rag)/gi, '')
    .replace(/Context:\s*\{[\s\S]*$/gi, '')
    .replace(/AI CISO assistant \(deterministic mode\)/gi, '')
    .replace(/Generative synthesis is blocked/gi, '')
    .trim()
}

export function isGenerationOutbound(pending: PendingDisclosure | null): boolean {
  if (!pending) return false
  return pending.purpose === GENERATION_PURPOSE || Boolean(pending.editable && pending.transmitted)
}

export function deriveProviderStatus(pending: PendingDisclosure | null, error: string | null): ProviderStatusKind {
  if (pending?.required && pending.requestId) return 'approval_required'
  if (error && /POLICY_BLOCKED|policy/i.test(error)) return 'policy_blocked'
  if (error && /not configured|NO_LLM|LOCAL_ONLY/i.test(error)) return 'not_configured'
  if (error && /unavailable|timeout|CONNECTION_RESET|DISPATCH_FAILED/i.test(error)) return 'temporarily_unavailable'
  if (pending?.providerKey || pending?.providerLabel) return 'external_available'
  return 'unknown'
}

export function sanitizeMessageForDisplay(msg: ConversationMessage): ConversationMessage {
  const diag = (msg.diagnostics || {}) as Record<string, unknown>
  const executionMetadata =
    msg.executionMetadata ||
    (diag.executionMetadata as ConversationMessage['executionMetadata']) ||
    null
  return {
    ...msg,
    content: typeof msg.content === 'string' ? msg.content : safeText(msg.content),
    executionMetadata,
    responseStatus: msg.responseStatus || (diag.responseStatus as string) || null,
    diagnostics: msg.diagnostics
      ? {
          providerType: diag.providerType,
          retrievalMode: diag.retrievalMode,
          actualProviderKey: diag.actualProviderKey,
          adapterType: diag.adapterType,
          simulated: diag.simulated,
          domainCounts: diag.domainCounts,
          ragRunId: diag.ragRunId,
          reasoningRunId: diag.reasoningRunId,
          answerType: diag.answerType || msg.answerType,
          displayMode: diag.displayMode || msg.displayMode,
          answerComposerVersion: diag.answerComposerVersion,
          answerQuality: diag.answerQuality,
          narrativeLayer: diag.narrativeLayer,
          executionMetadata,
          fallbackUsed: diag.fallbackUsed,
          fallbackReason: diag.fallbackReason,
          responseStatus: diag.responseStatus || msg.responseStatus,
        }
      : null,
  }
}

/** Compact attribution line from pipeline execution metadata (never LLM prose). */
export function formatExecutionAttribution(
  meta: ConversationMessage['executionMetadata'],
  t: (key: string, fallback?: string) => string,
): string | null {
  if (!meta) return null
  const ragOn = Boolean(meta.ragUsed)
  const ragLabel = ragOn
    ? t('complianceCenterUi.aiCiso.ragEnabled', 'RAG: enabled')
    : t('complianceCenterUi.aiCiso.ragOff', 'RAG: off')
  const provider = meta.providerDisplayName || meta.providerKey || 'Internal Engine'
  const mode = String(meta.executionMode || '')

  if (mode === 'EXTERNAL_LLM') {
    const modelPart = meta.model ? ` · Model: ${meta.model}` : ''
    // Show RAG only when grounding retrieval actually ran; otherwise omit misleading "RAG: enabled"
    const grounding =
      meta.ragUsed || (meta.contextSources || []).some((s) => s === 'standard_corpus' || s === 'standards_rag')
        ? ` · ${ragLabel}`
        : ''
    return `Provider: ${provider}${modelPart}${grounding}`
  }
  if (mode === 'INTERNAL_RAG') {
    return t(
      'complianceCenterUi.aiCiso.attrInternalRag',
      'Provider: Internal RAG · External LLM: not used',
    )
  }
  if (mode === 'INTERNAL_LOCAL_LLM') {
    const modelPart = meta.model ? ` · Model: ${meta.model}` : ''
    return `Provider: ${provider}${modelPart} · ${ragLabel}`
  }
  if (meta.fallbackUsed) {
    return t(
      'complianceCenterUi.aiCiso.attrFallback',
      'Provider: Internal Engine · Fallback',
    )
  }
  return t(
    'complianceCenterUi.aiCiso.attrInternal',
    'Provider: Internal Engine · Mode: Deterministic',
  )
}

export function formatResponseStatusLabel(
  status: string | null | undefined,
  insufficient: boolean,
  t: (key: string, fallback?: string) => string,
): string {
  const s = String(status || '')
  if (s === 'POLICY_BLOCKED') {
    return t('complianceCenterUi.aiCiso.statusPolicyBlocked', 'Blocked by policy')
  }
  if (s === 'PROVIDER_ERROR') {
    return t('complianceCenterUi.aiCiso.statusProviderError', 'Provider error')
  }
  if (s === 'GENERATION_FAILED') {
    return t('complianceCenterUi.aiCiso.statusGenerationFailed', 'Generation failed')
  }
  if (s === 'APPROVAL_REQUIRED') {
    return t('complianceCenterUi.aiCiso.statusApprovalRequired', 'Approval required')
  }
  if (s === 'INSUFFICIENT_DATA' || insufficient) {
    return t('complianceCenterUi.aiCiso.statusInsufficient', 'Insufficient data')
  }
  if (s === 'FALLBACK') {
    return t('complianceCenterUi.aiCiso.statusFallback', 'Internal fallback')
  }
  if (s === 'ANSWERED_EXTERNAL') {
    return t('complianceCenterUi.aiCiso.statusAnsweredExternal', 'Answered')
  }
  return t('complianceCenterUi.aiCiso.statusAnswered', 'Answered')
}

/** Soft-escape untrusted link-like content — responses render as text, not HTML. */
export function isSafeCitationUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return false
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}
